import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { MaskedText } from "../motion/MaskedText";

/** Inner-page hero: full-width image, navy overlay, masked title reveal, parallax. */
export function PageHero({ eyebrow, titleLines = [], subtitle, image, breadcrumb }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} data-testid="page-hero" className="relative min-h-[62vh] flex items-end overflow-hidden bg-navy">
      <motion.div style={reduce ? {} : { y }} className="absolute inset-0">
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover scale-110"
          loading="eager"
        />
        <div className="absolute inset-0 bg-navy/75" />
      </motion.div>

      <div className="relative w-full max-w-7xl mx-auto px-6 md:px-10 pb-16 pt-40">
        {breadcrumb && (
          <nav aria-label="Breadcrumb" className="mb-5 flex items-center gap-2 text-sm text-white/60">
            {breadcrumb.map((b, i) => (
              <span key={b.path} className="flex items-center gap-2">
                {i > 0 && <span className="text-white/30">/</span>}
                {i === breadcrumb.length - 1 ? (
                  <span className="text-accent2">{b.name}</span>
                ) : (
                  <Link to={b.path} className="hover:text-white transition-colors">{b.name}</Link>
                )}
              </span>
            ))}
          </nav>
        )}
        {eyebrow && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="block text-sm font-semibold uppercase tracking-[0.24em] text-accent2 mb-5"
          >
            {eyebrow}
          </motion.span>
        )}
        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white max-w-4xl">
          <MaskedText lines={titleLines} />
        </h1>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-7 max-w-2xl text-lg text-white/80 leading-relaxed"
          >
            {subtitle}
          </motion.p>
        )}
      </div>
    </section>
  );
}
